import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEye, FiCalendar, FiTag, FiBarChart2 } from 'react-icons/fi';
import { datosAPI } from '../utils/api';

const DetalleHistoricidad = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dato, setDato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef();

  useEffect(() => {
    cargarDato();
  }, [slug]);

  const cargarDato = async () => {
    try {
      setLoading(true);
      const response = await datosAPI.getPorId(slug);
      setDato(response.data);
      // Las visualizaciones se incrementan automáticamente en el backend
    } catch (error) {
      console.error('Error al cargar dato:', error);
      setError('No se pudo cargar el dato histórico');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !dato) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-600 text-lg mb-4">{error || 'Dato no encontrado'}</p>
        <Link to="/historicidad" className="text-purple-600 hover:underline">
          ← Volver a Historicidad
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/historicidad')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Volver a Historicidad
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Imagen destacada */}
          {dato.imagen && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={dato.imagen}
                alt={dato.tema}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8" ref={contentRef}>
            {/* Título */}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {dato.tema}
            </h1>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b">
              {dato.fechaEvento && (
                <span className="flex items-center">
                  <FiCalendar className="mr-1" />
                  Año {new Date(dato.fechaEvento).getFullYear()}
                </span>
              )}
              <span className="flex items-center">
                <FiEye className="mr-1" />
                {dato.visualizaciones || 0} vistas
              </span>
              {dato.usuarioInformante && (
                <span>Por {dato.usuarioInformante.nombre}</span>
              )}
            </div>

            {/* Porcentajes */}
            {(dato.porcentajeHistoricidad || dato.porcentajeAutenticidad || dato.porcentajeVeracidad) && (
              <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b">
                {dato.porcentajeHistoricidad && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {/*dato.porcentajeHistoricidad?.toFixed(0)*/}
                      {dato.porcentajeHistoricidad}%
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Historicidad</div>
                  </div>
                )}
                {dato.porcentajeAutenticidad && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {/*dato.porcentajeAutenticidad?.toFixed(0)*/}
                      {dato.porcentajeAutenticidad}%
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Autenticidad</div>
                  </div>
                )}
                {dato.porcentajeVeracidad && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {/*dato.porcentajeVeracidad?.toFixed(0)*/}
                      {dato.porcentajeVeracidad}%
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Veracidad</div>
                  </div>
                )}
              </div>
            )}

            {/* Catálogos */}
            {dato.catalogos && dato.catalogos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {dato.catalogos.map((catalogo) => (
                  <span
                    key={catalogo.id}
                    className="px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: catalogo.color }}
                  >
                    {catalogo.nombre}
                  </span>
                ))}
              </div>
            )}

            {/* Descripción */}
            <div className="prose prose-lg max-w-none mb-6">
              <p className="text-xl text-gray-600 leading-relaxed">
                {dato.descripcion}
              </p>
            </div>

            {/* Contenido principal */}
            <div 
              className="prose prose-lg max-w-none mb-8"
              dangerouslySetInnerHTML={{ __html: dato.textoHistorico }}
            />

            {/* Información adicional */}
            <div className="space-y-6 mt-8 pt-8 border-t">
              {/* Cita Bíblica */}
              {dato.citaBiblica && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-purple-900 mb-2 flex items-center">
                    <FiTag className="mr-2" />
                    Cita Bíblica
                  </h3>
                  <p className="text-gray-700">{dato.citaBiblica}</p>
                </div>
              )}

              {/* Strong Hebreo/Griego */}
              {(dato.strongHebreo || dato.strongGriego) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Referencias Strong</h3>
                  <div className="space-y-1 text-sm text-gray-700">
                    {dato.strongHebreo && <div><strong>Hebreo:</strong> {dato.strongHebreo}</div>}
                    {dato.strongGriego && <div><strong>Griego:</strong> {dato.strongGriego}</div>}
                  </div>
                </div>
              )}

              {/* Análisis */}
              {dato.analisisEncontrado && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-green-900 mb-2">Análisis</h3>
                  <p className="text-gray-700 text-sm">{dato.analisisEncontrado}</p>
                </div>
              )}

              {/* Hechos Históricos */}
              {dato.hechosHistoricos && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-yellow-900 mb-2">Hechos Históricos Relacionados</h3>
                  <p className="text-gray-700 text-sm">{dato.hechosHistoricos}</p>
                </div>
              )}

              {/* Referencias */}
              {dato.referencias && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Referencias</h3>
                  <p className="text-gray-700 text-sm">{dato.referencias}</p>
                </div>
              )}

              {/* Palabras Clave */}
              {dato.palabrasClave && (
                <div className="pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Palabras Clave</h3>
                  <div className="flex flex-wrap gap-2">
                    {dato.palabrasClave.split(',').map((palabra, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs"
                      >
                        {palabra.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default DetalleHistoricidad;







